import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { SpSurveyPath } from '../lib/sp-survey-paths'
import { isPendingSurveyUser } from '../lib/pending-survey-session'
import { useSessionStore } from '@/stores/useSessionStore'
import type {
  ProviderGetResponseData,
  ProviderStartResponseData,
  SurveySessionData,
} from '../lib/types'

async function fetchResponseState(responseId: string, pending: boolean): Promise<ProviderGetResponseData> {
  const { data } = await apiClient.get<ApiResponse<ProviderGetResponseData>>(
    pending ? SpSurveyPath.pendingState(responseId) : SpSurveyPath.responseState(responseId),
  )
  if (!data.data?.response) {
    throw new Error('Response state unavailable')
  }
  return data.data
}

async function fetchStartSession(instrumentId: string, pending: boolean): Promise<SurveySessionData> {
  const { data } = await apiClient.post<ApiResponse<ProviderStartResponseData>>(
    pending ? SpSurveyPath.pendingStart(instrumentId) : SpSurveyPath.responsesStart(instrumentId),
  )
  if (!data.data) {
    throw new Error('Could not start survey session')
  }
  return {
    response: data.data.response,
    definition: data.data.definition,
    resumed: data.data.resumed,
  }
}

export interface UseSurveySessionOptions {
  instrumentId: string | undefined
  responseId: string | undefined
  /** Definition from router state when navigating from Instrument Info. */
  initialDefinition?: unknown
  enabled?: boolean
}

export function useSurveySession({
  instrumentId,
  responseId,
  initialDefinition,
  enabled = true,
}: UseSurveySessionOptions) {
  const pendingUser = useSessionStore((s) => isPendingSurveyUser(s.user))
  return useQuery({
    queryKey:
      instrumentId && responseId
        ? queryKeys.spSurvey.session(instrumentId, responseId)
        : ['sp', 'survey', 'disabled'],
    enabled: Boolean(enabled && instrumentId && responseId),
    queryFn: async (): Promise<SurveySessionData> => {
      if (!instrumentId || !responseId) {
        throw new Error('Missing instrument or response id')
      }

      if (initialDefinition != null) {
        const loaded = await fetchResponseState(responseId, pendingUser)
        return {
          response: loaded.response,
          definition: loaded.definition ?? initialDefinition,
          resumed: loaded.response.answers.length > 0,
        }
      }

      const started = await fetchStartSession(instrumentId, pendingUser)
      if (started.response.responseId !== responseId) {
        const loaded = await fetchResponseState(responseId, pendingUser)
        return {
          response: loaded.response,
          definition: loaded.definition ?? started.definition,
          resumed: true,
        }
      }
      return started
    },
    staleTime: 0,
    refetchInterval: 15_000,
  })
}
